import { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { store } from 'Store';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Card, Form, Row, Col } from "react-bootstrap";
import PhoneInput from "react-phone-input-2";
import { TextButton } from "Components/Button";
import { setupMfa, submitMfaForBoth } from "../ResetPasswordAxiosFuncs";
import { setGlobalErrorStatus } from 'Components/StatusHandling';
import { ERROR_MESSAGES } from "Utils/constants";
import { PageInfo } from "Components/PageInformation/PageInformation";
import { PATHS } from "Utils/PagePaths";

const ResetPasswordSetupMFA = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token } = useParams();
  const [newPhoneRequest, setNewPhoneRequest] = useState({
    mobileNumber: null
  });
  const [mfaToken, setMfaToken] = useState("");
  const [showSendCodeMsg, setShowSendCodeMsg] = useState(false);
  const [disableSendCode, setDisableSendCode] = useState(true);
  const [disableMfaTokenField, setDisableMfaTokenField] = useState(true);
  // error messages display on top 
  const [globalErrorList, setGlobalErrorList] = useState([]);
  const tokenInputRef = useRef();

  // handle phone input field change
  const handlePhoneChange = (value) => {

    //console.log("print out phone change", value, value.length);

    // enable/disable "Send Code"
    if (value.length < 10) {
      setDisableSendCode(true);
    } else {
      setDisableSendCode(false);
    }

    setNewPhoneRequest({
      mobileNumber: value
    });
  }

  // api to send code with new phone number
  const handleSendCode = async () => {
    // clear error
    setGlobalErrorList([]);
    // disable the send code button to avoid double click renable it after 30s
    setDisableSendCode(true);

    const res = await setupMfa(token, newPhoneRequest)
      .catch(function (error) {
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: {
            redirect: false,
            redirectFirst: false,
            path: "",
            data: {}
          },
          error: error
        });
        return error.response;
      });;
    //console.log("handleSendCode()", res);

    if (res && res.data.status !== 1) {
      setGlobalErrorList(res.data.globalErrorMessageList);
      setDisableSendCode(false);
    } else {
      setTimeout(() => {
        setDisableSendCode(false);
      }, 30000);
      setShowSendCodeMsg(true);
      setDisableMfaTokenField(false);
      setTimeout(() => {
        tokenInputRef.current.focus();
      }, 500);
    }
  }

  // handle six digit mfa token field change
  const handleMfaChange = (e) => {

    let value = e.target.value;
    const regex = /^[0-9\b]+$/;

    //console.log("test mfa code", regex.test(value));

    if (value === '' || regex.test(value)) {
      setMfaToken(e.target.value);
    }
  }

  // api to verify the new phone device
  const verifyToken = async () => {
    // clear error
    setGlobalErrorList([]);
    setShowSendCodeMsg(false);

    const res = await submitMfaForBoth(token, mfaToken, 'SMS', true)
      .catch(function (error) {
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: {
            redirect: false,
            redirectFirst: false,
            path: "",
            data: {}
          },
          error: error
        });
        return error.response;
      });


    //console.log("verifyToken()", res);
    if (res && res.data.status !== 1) {
      setGlobalErrorList(res.data.globalErrorMessageList);
    } else {
      // navigate to reset password page
      navigate("/password/token/" + token + "/3", { state: { isForward: true } });
    }
  }

  // back to login
  const cancel = () => {
    setMfaToken("");
    navigate(PATHS.public['Sign In']);
  }

  return (
    <Row className="mb-3">
      <Col xl={10} lg={11} md={11} sm={12}>
        <Card id="resetpwd-sms-mfa-setup" className="p-4">
          <Card.Body className="text-start">
            <h1 id="mainContent" className="text-start mb-3 blue fw-bold">
              Two-Factor Authentication
            </h1>
            <hr></hr>
            {showSendCodeMsg ?
              <>
                <PageInfo className="mb-3" type="info" message={"A code has been sent to your registered cell phone. Please enter that code in the field below. You may send a new code after 30 seconds if needed."}></PageInfo>
                <br />
              </>
              : null}
            {globalErrorList.map((item, index) =>
              <>
                <PageInfo key={"errors_" + index} type="error" message={item}></PageInfo>
                <br />
              </>
            )}
            <p className="text-start pb-2">
              To set up you authentication phone number, follow the steps below.
            </p>
            <Form>
              <Form.Group as={Row} className="mb-3" controlId="mfaPhone">
                <Form.Label column sm="5" className="mfa-setting-label">
                  1. Enter a ten digit phone number
                </Form.Label>
                <Col sm={3} xs={9}>
                  <PhoneInput
                    defaultMask="(...) ...-...."
                    alwaysDefaultMask="true"
                    country={"us"}
                    autoFormat="true"
                    enableAreaCodes="true"
                    disableCountryCode="true"
                    disableDropdown="true"
                    disableSearchIcon="true"
                    disableCountryGuess="true"
                    buttonStyle={{ display: 'none' }}
                    inputClass={"form-control"}
                    placeholder={""}
                    inputProps={{
                      id: "mfaPhone"
                    }}
                    onChange={handlePhoneChange}
                  />
                </Col>
              </Form.Group>
              <Row className="mb-4 align-items-center">
                <Col sm="5" className="pt-2 mfa-setting-label">
                  <div class="mfa-setting-step">
                    <span>2.</span>
                    <span>Click to send code to your registered mobile device.</span>
                  </div>
                </Col>
                <Col>
                  <TextButton
                    className="mt-1"
                    disabled={disableSendCode}
                    variant="normal"
                    onClick={handleSendCode}
                    content={"Send Code"}
                  >
                  </TextButton>
                </Col>
              </Row >
              <Form.Group as={Row} className="mb-3" controlId="mfaToken">
                <Form.Label column sm="5" className="mfa-setting-label">
                  3. Enter the six digit code
                </Form.Label>
                <Col sm={3} xs={9}>
                  <Form.Control
                    ref={tokenInputRef}
                    disabled={disableMfaTokenField}
                    type="text"
                    maxlength={6}
                    value={mfaToken}
                    onChange={handleMfaChange}
                  />
                </Col>
              </Form.Group>
              <Row className="mb-3 pt-1 align-items-center">
                <Col sm="5" className="pt-2 mfa-setting-label">4. Click Verify to confirm</Col>
                <Col className="mt-1">
                  <TextButton
                    className="w-10"
                    variant="normal"
                    disabled={mfaToken.length !== 6}
                    onClick={verifyToken}
                    content={"Verify"}
                  >
                  </TextButton>
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  <TextButton
                    className="w-10"
                    variant="outline"
                    onClick={cancel}
                    content={"Cancel"}
                  >
                  </TextButton>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default ResetPasswordSetupMFA;